import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { useSeoMeta } from '@/hooks/useSeoMeta';

export default function BrandsPage() {
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/catalog/brands')
      .then(r => setBrands(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useSeoMeta({ seo_title: 'Brands We Authenticate - The Fashion Detective', seo_description: 'Browse our directory of luxury brands. Expert authentication for Louis Vuitton, Chanel, Gucci, Hermes, and more.' });

  const filtered = brands.filter(b => b.name.toLowerCase().includes(search.toLowerCase()));
  const letters = [...new Set(filtered.map(b => b.name[0].toUpperCase()))].sort();

  return (
    <div>
      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, hsl(36 33% 98%) 0%, hsl(36 22% 94%) 55%, hsl(35 19% 88%) 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-2">Brands</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-[-0.02em] text-ink mb-4">Brands We Authenticate</h1>
            <p className="text-sm sm:text-base text-muted-foreground max-w-xl mb-8">Browse our directory of luxury brands. We authenticate items from the world's most prestigious fashion houses.</p>
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search brands..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white"
                data-testid="brands-search-input"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brand list */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-24 bg-stone/20 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {letters.map(letter => (
                <div key={letter} className="mb-8">
                  <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3 font-medium">{letter}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.filter(b => b.name[0].toUpperCase() === letter).map(brand => (
                      <Link key={brand.id} to={`/brands/${brand.slug}`} className="no-underline" data-testid={`brand-link-${brand.slug}`}>
                        <Card className="bg-white border-stone/50 hover:shadow-[var(--shadow-pop)] transition-shadow duration-200">
                          <CardContent className="p-4 flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-medium text-ink">{brand.name}</h3>
                            </div>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-10">No brands found matching "{search}"</p>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
